// NIGHT-DIRECTSTAFF-2026-10-19 §4: "Looks like the same person" on a lead's detail -- the other leads with the same phone
// or email, and (once leads.merged_into exists) a one-tap merge that keeps the OLDER lead, asks first, and can be undone
// for the rest of the session. lib/duplicates.js does the writes.
import { useState } from 'react';
import { lookAlikes, matchedOn, olderNewer, fillFrom } from './lib/duplicates';
import { signedUpFor } from './lib/signedUp';
import { leadHref } from './lib/leadSearch';
import { VIA_WORDS } from './lib/personMatch';

const btn = (bg, fg, off) => ({ background: bg, color: fg, borderRadius: 8, padding: '0 14px', minHeight: 44, fontSize: 13, fontWeight: 700, border: 0,
  cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1 });
const id8 = (l) => String(l.id).slice(0, 8);
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

/**
 * lead: the lead on screen; allLeads; canMerge: the columns exist; merge(a, b) -> the done record; undo(done);
 * undoable: merges made this session that involve this lead (newest first)
 */
// NIGHT-DIRECTSTAFF-2026-10-23 §4: candidate = candidateMatch(lead, ...) -- the candidate this lead belongs to. Shown when the match is
// by phone (an email match is already plain to see), so a phone-only lead says whose it is.
export default function DuplicateStrip({ lead, allLeads, canMerge = false, merge, undo, undoable = [], toast = () => {}, candidate = null }) {
  const [asking, setAsking] = useState(null);   // the other lead
  const [busy, setBusy] = useState(false);
  const others = lookAlikes(lead, allLeads);
  const byPhone = candidate && candidate.via !== 'email' ? candidate : null;
  if (!others.length && !undoable.length && !byPhone) return null;
  const go = async (other) => {
    setBusy(true);
    try { await merge(lead, other); setAsking(null); toast('Merged: the older lead kept, the newer one hidden (Undo is on the strip)'); }
    catch (e) { toast(e.message || 'Could not merge'); } finally { setBusy(false); }
  };
  const back = async (done) => {
    setBusy(true);
    try { await undo(done); toast('Undone: both leads stand on their own again'); } catch (e) { toast(e.message || 'Could not undo'); } finally { setBusy(false); }
  };
  return (
    <section data-testid="dup-strip" className="card" style={{ padding: 12, marginBottom: 12, background: '#fefce8', border: '1px solid #fde68a' }}>
      {(others.length > 0 || byPhone) && <h3 style={{ fontSize: 14, fontWeight: 800, margin: '0 0 6px', color: '#713f12' }}>Looks like the same person</h3>}
      {byPhone && (
        <p data-testid="dup-candidate" style={{ fontSize: 13, margin: '0 0 6px', overflowWrap: 'anywhere' }}>
          Candidate <strong>{byPhone.candidate.name || 'No name'}</strong> · <em>{VIA_WORDS[byPhone.via]}</em> · this lead is on their record and timeline.
        </p>
      )}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {others.map((o) => {
          const { older, newer } = olderNewer(lead, o);
          const thisIsOlder = older.id === lead.id;
          const { filled } = fillFrom(older, newer);
          return (
            <li key={o.id} data-testid={`dup-${id8(o)}`} style={{ padding: '6px 0', borderTop: '1px solid #fde68a', fontSize: 13 }}>
              <div style={{ overflowWrap: 'anywhere' }}>
                <strong>{o.name || 'No name'}</strong> · <code>{id8(o)}</code> · came in {day(o.created_at)} · {signedUpFor(o).label} · <em>same {matchedOn(lead, o)}</em>
                {' '}<a href={leadHref(o.id)} data-testid={`dup-open-${id8(o)}`} style={{ color: '#1d4ed8', display: 'inline-block', minHeight: 44, lineHeight: '44px', padding: '0 4px' }}>Open</a>
              </div>
              {canMerge && asking !== o.id && (
                <button className="nb" data-testid={`dup-merge-${id8(o)}`} disabled={busy} onClick={() => setAsking(o.id)} style={btn('#713f12', 'white', busy)}>
                  {thisIsOlder ? `Merge ${id8(o)} into this one` : `Merge into ${id8(o)} (the older one)`}</button>
              )}
              {asking === o.id && (
                <div data-testid="dup-confirm" role="alertdialog" aria-label="Confirm the merge" style={{ background: 'white', border: '1px solid #fde68a', borderRadius: 8, padding: 10, marginTop: 6 }}>
                  <p style={{ margin: '0 0 8px' }}>Keep <code>{id8(older)}</code> (the older lead). <code>{id8(newer)}</code> is marked as merged into it and leaves the lists;
                    {filled.length ? ` the older lead's blank ${filled.join(', ')} ${filled.length === 1 ? 'is' : 'are'} filled from it;` : ''} nothing is deleted, and you can undo it until you close the CRM.</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="nb" data-testid="dup-confirm-yes" disabled={busy} onClick={() => go(o)} style={btn('#713f12', 'white', busy)}>{busy ? 'Merging…' : 'Merge'}</button>
                    <button className="nb" data-testid="dup-confirm-no" disabled={busy} onClick={() => setAsking(null)} style={btn('#f1f5f9', '#475569', busy)}>Cancel</button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {!canMerge && others.length > 0 && <p data-testid="dup-not-yet" style={{ fontSize: 12, color: '#713f12', margin: '6px 0 0' }}>Merging turns on once the database update is applied.</p>}
      {undoable.map((d) => (
        <p key={d.newer.id} data-testid={`dup-undo-${id8(d.newer)}`} style={{ margin: '8px 0 0', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span><code>{id8(d.newer)}</code> merged into <code>{id8(d.older)}</code>.</span>
          <button className="nb" data-testid={`dup-undo-btn-${id8(d.newer)}`} disabled={busy} onClick={() => back(d)} style={btn('#f1f5f9', '#713f12', busy)}>Undo</button>
        </p>
      ))}
    </section>
  );
}
